import * as React from "react";
import { useEffect } from "react";
import { IS_CART_OPEN } from "@/helpers/constants";
import { useCartUiActions } from "@/features/cart";
import { track } from "@/helpers/googleAnalytics";
import { shopifyCartApi } from "@/services/shopifyCart";

// Due to the possibility of there being forms managed outside of react,
// we hook into the global event submit listener for all cart adds
const useOpenCartDrawerOnCartAdd = () => {
  const { openDrawer } = useCartUiActions();
  const addItemToCart = shopifyCartApi.useAdd();

  useEffect(() => {
    function handleFormSubmission(ev: SubmitEvent) {
      if (ev.defaultPrevented || !(ev.target instanceof HTMLFormElement)) {
        return;
      }

      if (ev.target.action.endsWith("/cart/add")) {
        ev.preventDefault();
        const submittedForm = ev.target;
        addItemToCart.mutate(new FormData(submittedForm), {
          onSuccess: () => {
            openDrawer();
            submittedForm.dispatchEvent(
              new CustomEvent("cart:add:success", {
                bubbles: true,
              })
            );
          },
        });
      }
    }

    window.addEventListener("submit", handleFormSubmission);
    return () => {
      window.removeEventListener("submit", handleFormSubmission);
    };
  }, [openDrawer, addItemToCart]);
};

const useCartUrls = () => {
  const { openDrawer } = useCartUiActions();

  // Track current page in sessionStorage (except when on /cart)
  useEffect(() => {
    const currentPath = window.location.pathname;
    if (currentPath !== "/cart") {
      // Only store the pathname to avoid issues with query params that might become invalid
      sessionStorage.setItem("previousPage", currentPath);
    }
  });

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    if (searchParams.get(IS_CART_OPEN) === "true") {
      openDrawer();
      // Remove the cart_isOpen param from URL
      searchParams.delete(IS_CART_OPEN);
      const newUrl = searchParams.toString()
        ? `${window.location.pathname}?${searchParams.toString()}`
        : window.location.pathname;
      window.history.replaceState({}, "", newUrl);
    }
  }, [openDrawer]);
};

const useTotals = () => {
  const totalPrice = shopifyCartApi.useFetch().data?.items_subtotal_price;
  useEffect(() => {
    if (totalPrice) {
      track({ name: "Update Cart Total", total: totalPrice });
    }
  }, [totalPrice]);
};

type CartProviderProps = {
  children: React.ReactNode;
};

export const CartProvider = ({ children }: CartProviderProps) => {
  useOpenCartDrawerOnCartAdd();
  useCartUrls();
  useTotals();
  return <>{children}</>;
};
